import React, { useState, useEffect, useMemo, useContext } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Autocomplete,
  Alert,
  Card,
  CardContent,
  Chip,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { Search, Download, Refresh, TableChart } from "@mui/icons-material";
import axios from "axios";
import { format } from "date-fns";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";

// Client and place are read back out of trip_booking, so those lists grow with
// every new customer an established store has ever driven for. MUI paints every
// match into the DOM - it does not virtualise - so cap what one open can render.
// Typing narrows the list long before the cap is reached.
const limitedOptions = createFilterOptions({ limit: 100 });

const TripReport = () => {
  const { userInfo } = useContext(AutoTaskAuthContext) || {};
  const store_id = userInfo?.store_id;

  // ─── Filter options ───────────────────────────────────────────────────────
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [clients, setClients] = useState([]);
  const [places, setPlaces] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // ─── Selected filters (every one optional) ────────────────────────────────
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedPlace, setSelectedPlace] = useState(null);

  const [fromDate, setFromDate] = useState(
    format(
      new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      "yyyy-MM-dd",
    ),
  );
  const [toDate, setToDate] = useState(format(new Date(), "yyyy-MM-dd"));

  const [loadingReport, setLoadingReport] = useState(false);
  const [reportData, setReportData] = useState(null);
  // The filters that produced `reportData` — so the heading keeps describing
  // what is on screen even after the dropdowns are changed but not re-run.
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [error, setError] = useState("");

  // ─── Helpers ──────────────────────────────────────────────────────────────
  const formatTime = (timeStr) => {
    if (!timeStr) return null;
    try {
      return format(new Date(`1970-01-01T${timeStr}`), "hh:mm a");
    } catch {
      return null;
    }
  };

  const formatDate = (value) => {
    if (!value) return null;
    try {
      return format(new Date(value), "dd-MM-yyyy");
    } catch {
      return null;
    }
  };

  // "12-08-2026 09:30 AM" on one line for exports, two lines in the PDF
  const dateTimeText = (date, time, separator = " ") => {
    const d = formatDate(date);
    if (!d) return "-";
    const t = formatTime(time);
    return t ? `${d}${separator}${t}` : d;
  };

  // "12-08" — day-month only, used on screen where the period is already in
  // the header. Exports keep the year so a saved file stays unambiguous.
  const formatDayMonth = (value) => {
    if (!value) return null;
    try {
      return format(new Date(value), "dd-MM");
    } catch {
      return null;
    }
  };

  // "12-08 09:30 AM"
  const dateTimeCompact = (date, time) => {
    const d = formatDayMonth(date);
    if (!d) return "-";
    const t = formatTime(time);
    return t ? `${d} ${t}` : d;
  };

  const formatAmount = (amount) =>
    new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount ?? 0);

  // ─── Load filter options ──────────────────────────────────────────────────
  useEffect(() => {
    if (!store_id) return;

    const loadOptions = async () => {
      setLoadingOptions(true);
      const base = process.env.REACT_APP_API_BASE_URL;
      try {
        const [vehicleRes, driverRes, filterRes] = await Promise.all([
          axios.get(`${base}/api/travel/vehicles/store/${store_id}`),
          axios.get(`${base}/api/travel/drivers/store/${store_id}`),
          axios.get(`${base}/api/accounts/trip-report/filters/${store_id}`),
        ]);

        if (vehicleRes.data?.success) setVehicles(vehicleRes.data.data || []);
        if (driverRes.data?.success) setDrivers(driverRes.data.data || []);
        if (filterRes.data?.success) {
          setClients(filterRes.data.data?.clients || []);
          setPlaces(filterRes.data.data?.places || []);
        }
      } catch (err) {
        console.error("Error loading trip report filters:", err);
        setError("Error loading filter options");
      } finally {
        setLoadingOptions(false);
      }
    };

    loadOptions();
  }, [store_id]);

  // ─── Generate ─────────────────────────────────────────────────────────────
  const handleGenerateReport = async () => {
    setError("");
    if (!fromDate || !toDate) {
      setError("Please select a date range");
      return;
    }
    if (fromDate > toDate) {
      setError("From Date cannot be after To Date");
      return;
    }

    try {
      setLoadingReport(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/accounts/trip-report`,
        {
          store_id: parseInt(store_id),
          from_date: fromDate,
          to_date: toDate,
          // Omitted filters simply widen the report
          vehicle_id: selectedVehicle?.vehicle_id ?? null,
          driver_id: selectedDriver?.id ?? null,
          client: selectedClient || null,
          place: selectedPlace || null,
        },
      );

      if (response.data.success) {
        setReportData(response.data.data);
        setAppliedFilters({
          vehicle: selectedVehicle,
          driver: selectedDriver,
          client: selectedClient,
          place: selectedPlace,
          fromDate,
          toDate,
        });
      } else {
        setError(response.data.message || "Failed to generate report");
      }
    } catch (err) {
      console.error("Error generating trip report:", err);
      setError(err.response?.data?.message || "Error generating trip report");
    } finally {
      setLoadingReport(false);
    }
  };

  const handleReset = () => {
    setSelectedVehicle(null);
    setSelectedDriver(null);
    setSelectedClient(null);
    setSelectedPlace(null);
    setReportData(null);
    setAppliedFilters(null);
    setError("");
  };

  // ─── What the report is currently showing ─────────────────────────────────
  const scope = useMemo(() => {
    if (!appliedFilters) return null;
    const { vehicle, driver, client, place } = appliedFilters;

    const parts = [];
    if (vehicle)
      parts.push(`Vehicle ${vehicle.vehicle_code || vehicle.vehicle_name || ""}`);
    if (driver) parts.push(`Driver ${driver.name || ""}`);
    if (client) parts.push(`Client ${client}`);
    if (place) parts.push(`Place ${place}`);

    let title;
    if (vehicle && driver) {
      title = "Vehicle + Driver Trip Report";
    } else if (vehicle) {
      title = "Vehicle-wise Trip Report";
    } else if (driver) {
      title = "Driver-wise Trip Report";
    } else {
      title = "Trip Report";
    }

    return {
      title,
      // Spelled out so it is obvious a combined filter is an intersection
      note:
        vehicle && driver
          ? "Only trips where this driver drove this vehicle"
          : null,
      label: parts.length > 0 ? parts.join("  •  ") : "All vehicles & drivers",
      parts,
    };
  }, [appliedFilters]);

  const trips = reportData?.trips || [];

  const summary = reportData
    ? {
        totalTrips: reportData.total_trips ?? 0,
        totalKmRun: reportData.total_km_run ?? 0,
        totalBata: reportData.total_bata ?? 0,
        totalQuote: reportData.total_quote ?? 0,
        totalHire: reportData.total_hire ?? 0,
      }
    : null;

  const fileStem = useMemo(() => {
    const bits = ["Trip_Report"];
    if (appliedFilters?.vehicle)
      bits.push(appliedFilters.vehicle.vehicle_code || "Vehicle");
    if (appliedFilters?.driver)
      bits.push(appliedFilters.driver.name || "Driver");
    bits.push(
      `${format(new Date(fromDate), "dd-MM-yyyy")}_to_${format(
        new Date(toDate),
        "dd-MM-yyyy",
      )}`,
    );
    return bits.join("_").replace(/[^a-zA-Z0-9_]/g, "_");
  }, [appliedFilters, fromDate, toDate]);

  // Single source of truth for the report columns, so the on-screen table,
  // the PDF and the Excel sheet can never drift apart.
  const COLUMNS = [
    { key: "sl", label: "#", align: "center", width: 8 },
    { key: "entry_date", label: "Entry Date", align: "left", width: 20 },
    { key: "vehicle", label: "Vehicle", align: "left", width: 20 },
    { key: "driver", label: "Driver", align: "left", width: 24 },
    { key: "hire", label: "Hire", align: "right", width: 18 },
    { key: "from", label: "From", align: "left", width: 26 },
    { key: "to", label: "To", align: "left", width: 26 },
    { key: "opening", label: "Opening", align: "right", width: 18 },
    { key: "closing", label: "Closing", align: "right", width: 18 },
    { key: "running", label: "Running", align: "right", width: 18 },
    { key: "client", label: "Client", align: "left", width: 30 },
    { key: "place", label: "Place", align: "left", width: 26 },
  ];

  // compact -> "12-08 09:30 AM" for the on-screen table.
  // Otherwise full "12-08-2026 09:30 AM" for PDF/Excel, where `separator`
  // can be "\n" to stack the date and time in a PDF cell.
  const rowValues = (trip, idx, { compact = false, separator = " " } = {}) => ({
    sl: idx + 1,
    entry_date: compact
      ? formatDayMonth(trip.entry_date) || "-"
      : formatDate(trip.entry_date) || "-",
    from: compact
      ? dateTimeCompact(trip.pickup_date, trip.pickup_time)
      : dateTimeText(trip.pickup_date, trip.pickup_time, separator),
    to: compact
      ? dateTimeCompact(trip.drop_date, trip.drop_time)
      : dateTimeText(trip.drop_date, trip.drop_time, separator),
    vehicle: trip.vehicle_code || "-",
    opening: trip.opening_km !== null ? formatAmount(trip.opening_km) : "-",
    closing: trip.closing_km !== null ? formatAmount(trip.closing_km) : "-",
    running: trip.running_km !== null ? formatAmount(trip.running_km) : "-",
    client: trip.client || "-",
    place: trip.place || "-",
    driver: trip.driver_name || "-",
    hire: formatAmount(trip.hire ?? 0),
  });

  // ─── PDF Export ───────────────────────────────────────────────────────────
  const generatePDF = () => {
    if (trips.length === 0) {
      setError("No data available to export");
      return;
    }

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;

    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text(userInfo?.store_name || "", pageWidth / 2, 12, {
      align: "center",
    });

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(scope?.title || "Trip Report", pageWidth / 2, 18, {
      align: "center",
    });

    doc.setFontSize(8);
    doc.text(scope?.label || "", pageWidth / 2, 23, { align: "center" });
    doc.text(
      `Period: ${format(new Date(fromDate), "dd-MM-yyyy")} to ${format(
        new Date(toDate),
        "dd-MM-yyyy",
      )}`,
      margin,
      29,
    );

    const body = trips.map((trip, idx) => {
      const v = rowValues(trip, idx, { separator: "\n" });
      return COLUMNS.map((c) => v[c.key]);
    });

    // Totals line — only the three numeric columns carry a total
    const totalRow = COLUMNS.map((c) => {
      if (c.key === "entry_date")
        return { content: "TOTAL", styles: { fontStyle: "bold" } };
      if (c.key === "running" || c.key === "hire")
        return {
          content: formatAmount(
            c.key === "running" ? summary.totalKmRun : summary.totalHire,
          ),
          styles: { fontStyle: "bold", halign: "right" },
        };
      return "";
    });

    doc.autoTable({
      startY: 34,
      head: [
        COLUMNS.map((c) => ({
          content: c.label,
          styles: { fontStyle: "bold", halign: c.align },
        })),
      ],
      body: [...body, totalRow],
      theme: "grid",
      styles: {
        fontSize: 7,
        cellPadding: 1.5,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
        textColor: [0, 0, 0],
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        lineWidth: 0.3,
        fontSize: 7,
      },
      columnStyles: COLUMNS.reduce(
        (acc, c, i) => ({
          ...acc,
          [i]: { halign: c.align, cellWidth: c.width },
        }),
        {},
      ),
      margin: { left: margin, right: margin },
    });

    doc.save(`${fileStem}.pdf`);
  };

  // ─── Excel Export ─────────────────────────────────────────────────────────
  const generateExcel = () => {
    if (trips.length === 0) {
      setError("No data available to export");
      return;
    }

    const excelData = trips.map((trip, idx) => {
      const v = rowValues(trip, idx);
      return COLUMNS.reduce(
        (acc, c) => ({ ...acc, [c.label === "#" ? "Sl No" : c.label]: v[c.key] }),
        {},
      );
    });

    excelData.push(
      COLUMNS.reduce((acc, c) => {
        const key = c.label === "#" ? "Sl No" : c.label;
        if (c.key === "entry_date") return { ...acc, [key]: "TOTAL" };
        if (c.key === "running")
          return { ...acc, [key]: summary.totalKmRun || 0 };
        if (c.key === "hire") return { ...acc, [key]: summary.totalHire || 0 };
        return { ...acc, [key]: "" };
      }, {}),
    );

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    worksheet["!cols"] = Object.keys(excelData[0]).map((key) => ({
      wch: Math.max(key.length + 4, 12),
    }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Trip Report");
    XLSX.writeFile(workbook, `${fileStem}.xlsx`);
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* Filters */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: 1.5,
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="h6">Report Filters</Typography>
          <Typography variant="caption" color="text.secondary">
            Every filter is optional — pick a vehicle for a vehicle-wise
            report, a driver for a driver-wise report, or both to see only the
            trips where that driver drove that vehicle.
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              type="date"
              label="From Date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              type="date"
              label="To Date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Autocomplete
              size="small"
              filterOptions={limitedOptions}
              options={vehicles}
              value={selectedVehicle}
              loading={loadingOptions}
              onChange={(_, v) => setSelectedVehicle(v)}
              getOptionLabel={(o) =>
                [o.vehicle_code, o.vehicle_name].filter(Boolean).join(" - ") ||
                ""
              }
              isOptionEqualToValue={(o, v) => o.vehicle_id === v?.vehicle_id}
              renderInput={(params) => (
                <TextField {...params} label="Vehicle" placeholder="All" />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Autocomplete
              size="small"
              filterOptions={limitedOptions}
              options={drivers}
              value={selectedDriver}
              loading={loadingOptions}
              onChange={(_, v) => setSelectedDriver(v)}
              getOptionLabel={(o) =>
                [o.driver_code || o.code, o.name].filter(Boolean).join(" - ") ||
                ""
              }
              isOptionEqualToValue={(o, v) => o.id === v?.id}
              renderInput={(params) => (
                <TextField {...params} label="Driver" placeholder="All" />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Autocomplete
              size="small"
              filterOptions={limitedOptions}
              options={clients}
              value={selectedClient}
              loading={loadingOptions}
              onChange={(_, v) => setSelectedClient(v)}
              renderInput={(params) => (
                <TextField {...params} label="Client" placeholder="All" />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Autocomplete
              size="small"
              filterOptions={limitedOptions}
              options={places}
              value={selectedPlace}
              loading={loadingOptions}
              onChange={(_, v) => setSelectedPlace(v)}
              renderInput={(params) => (
                <TextField {...params} label="Place" placeholder="All" />
              )}
            />
          </Grid>

          <Grid item xs={12} sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              startIcon={
                loadingReport ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <Search />
                )
              }
              onClick={handleGenerateReport}
              disabled={loadingReport}
            >
              {loadingReport ? "Loading..." : "Filter"}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleReset}
            >
              Reset
            </Button>
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>

      {/* Report */}
      {reportData && (
        <Paper elevation={2} sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {scope?.title}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                alignItems: "center",
                mt: 1,
              }}
            >
              {scope?.parts.length > 0 ? (
                scope.parts.map((p) => (
                  <Chip key={p} label={p} size="small" color="primary" />
                ))
              ) : (
                <Chip label="All vehicles & drivers" size="small" />
              )}
              <Chip
                label={`${format(new Date(fromDate), "dd/MM/yyyy")} — ${format(
                  new Date(toDate),
                  "dd/MM/yyyy",
                )}`}
                size="small"
                variant="outlined"
              />
            </Box>
            {scope?.note && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1 }}
              >
                {scope.note}
              </Typography>
            )}
          </Box>

          {/* Summary */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: "Total Trips", value: formatAmount(summary.totalTrips) },
              { label: "Total KM Run", value: formatAmount(summary.totalKmRun) },
              { label: "Total Hire", value: formatAmount(summary.totalHire) },
              { label: "Total Bata", value: formatAmount(summary.totalBata) },
              { label: "Total Quote", value: formatAmount(summary.totalQuote) },
            ].map((card) => (
              // 2.4 = 12/5, so all five cards sit on one row on desktop
              <Grid item xs={12} sm={6} md={2.4} key={card.label}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      {card.label}
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 600, color: "primary.main" }}
                    >
                      {card.value}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Export */}
          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={generatePDF}
              disabled={trips.length === 0}
            >
              PDF
            </Button>
            <Button
              variant="outlined"
              startIcon={<TableChart />}
              onClick={generateExcel}
              disabled={trips.length === 0}
            >
              Excel
            </Button>
          </Box>

          {/* Table */}
          <TableContainer sx={{ maxHeight: 560 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {COLUMNS.map((c) => (
                    <TableCell
                      key={c.key}
                      align={c.align}
                      sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
                    >
                      {c.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {trips.map((trip, idx) => {
                  const v = rowValues(trip, idx, { compact: true });
                  return (
                    <TableRow key={trip.booking_id || idx} hover>
                      {COLUMNS.map((c) => (
                        <TableCell
                          key={c.key}
                          align={c.align}
                          sx={{
                            whiteSpace:
                              c.key === "from" || c.key === "to"
                                ? "nowrap"
                                : "normal",
                          }}
                        >
                          {v[c.key]}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}

                {trips.length > 0 && (
                  <TableRow>
                    {COLUMNS.map((c) => (
                      <TableCell
                        key={c.key}
                        align={c.align}
                        sx={{ fontWeight: 700, borderTop: "2px solid #ccc" }}
                      >
                        {c.key === "entry_date"
                          ? "TOTAL"
                          : c.key === "running"
                          ? formatAmount(summary.totalKmRun)
                          : c.key === "hire"
                          ? formatAmount(summary.totalHire)
                          : ""}
                      </TableCell>
                    ))}
                  </TableRow>
                )}

                {trips.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={COLUMNS.length} align="center">
                      <Typography sx={{ py: 3 }} color="text.secondary">
                        No trips found for these filters
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default TripReport;
